import React, { useState, useEffect } from 'react';
import styles from './ReservationSummary.module.css';
import api from '../../services/api';
import { useTranslation } from '../../i18n/useTranslation';
import { hasReservationSummaryData } from './reservationSummaryVisibility';

/**
 * ReservationSummary - 예약 요약 위젯
 * 이번달/이번주/오늘/다음예약/선금미확인 표시
 */
const ReservationSummary = () => {
    const { t, lang } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const result = await api.get('/manual-reservations/summary');
                setData(result);
            } catch (err) {
                console.error('ReservationSummary fetch error:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchSummary();
    }, []);

    if (loading) {
        return <div className={styles.container}>...</div>;
    }

    if (error || !data) {
        return null; // 에러 시 숨김
    }

    // 다음 예약 날짜 포맷 (언어별)
    const formatNextDate = (reservation) => {
        if (!reservation) return null;
        const date = new Date(reservation.date);

        // 언어별 로케일 매핑
        const localeMap = {
            ko: 'ko-KR',
            en: 'en-US',
            fr: 'fr-FR'
        };
        const locale = localeMap[lang] || 'fr-FR';

        // 언어별 날짜 포맷
        const formattedDate = date.toLocaleDateString(locale, {
            month: 'short',
            day: 'numeric'
        });

        return `${formattedDate} ${reservation.time}`;
    };

    // 표시할 요약 데이터가 전혀 없으면 숨김
    if (!hasReservationSummaryData(data)) {
        return null;
    }

    const count = t('summary.count');

    return (
        <div className={styles.container}>
            {/* 첫 줄: 이번달 · 이번주 · 오늘 */}
            <div className={styles.row}>
                {data.thisMonth > 0 && (
                    <span className={styles.item}>
                        {t('summary.thisMonth')} <span className={styles.count}>{data.thisMonth}</span>{count}
                    </span>
                )}
                {data.thisWeek > 0 && (
                    <span className={styles.item}>
                        {t('summary.thisWeek')} <span className={styles.count}>{data.thisWeek}</span>{count}
                    </span>
                )}
                {data.today?.count > 0 && (
                    <span className={styles.item}>
                        {t('summary.today')} <span className={styles.count}>{data.today.count}</span>{count}
                        {data.today.firstStartTime && (
                            <span className={styles.time}> ({data.today.firstStartTime}~)</span>
                        )}
                    </span>
                )}
            </div>

            {/* 둘째 줄: 다음 예약 */}
            {data.nextReservation && (
                <div className={styles.row}>
                    <span className={styles.next}>
                        {t('summary.next')}: {formatNextDate(data.nextReservation)}
                    </span>
                </div>
            )}

            {/* 셋째 줄: 선금 미확인 (경고) */}
            {data.pendingDeposit > 0 && (
                <div className={styles.row}>
                    <span className={styles.warning}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        {t('summary.pendingDeposit')} {data.pendingDeposit}{count}
                    </span>
                </div>
            )}
        </div>
    );
};

export default ReservationSummary;
